import cn from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import fullscreenExitSvg from "../assets/fullscreen-exit.svg";
import fullscreenSvg from "../assets/fullscreen.svg";
import helpSvg from "../assets/help.svg";
import settingsSvg from "../assets/settings.svg";
import statsSvg from "../assets/stats.svg";
import { NUM_BOARDS, NUM_GUESSES } from "../consts";
import { formatTimeElapsed, MersenneTwister } from "../funcs";
import { loadGameFromLocalStorage } from "../serialize";
import HeaderScroll from "./HeaderScroll";
import Switcher from "./Switcher";
import {
  showAboutPopup,
  showSettingsPopup,
  showStatsPopup,
  startPracticeGame,
  startGame,
  useSelector,
} from "../store";

// Declare typescript definitions for safari fullscreen stuff
declare global {
  interface Document {
    webkitFullscreenElement: Element | null;
    webkitExitFullscreen: () => void;
  }
  interface HTMLElement {
    webkitRequestFullscreen: () => void;
  }
}
function isFullscreen() {
  const element =
    document.fullscreenElement || document.webkitFullscreenElement;
  return Boolean(element);
}
function enterFullscreen() {
  const element = document.documentElement;
  if (element.requestFullscreen) {
    element.requestFullscreen();
  } else if (element.webkitRequestFullscreen) {
    element.webkitRequestFullscreen();
  }
}
function exitFullscreen() {
  if (document.exitFullscreen) {
    document.exitFullscreen();
  } else if (document.webkitExitFullscreen) {
    document.webkitExitFullscreen();
  }
}

export default function Header() {
  const dispatch = useDispatch();
  const id = useSelector((s) => s.game.id);
  const targets = useSelector((s) => s.game.targets);
  const guesses = useSelector((s) => s.game.guesses);
  const boardsCompleted = useMemo(
    () =>
      targets
        .map((target) => guesses.indexOf(target) !== -1)
        .reduce((a, v) => a + (v ? 1 : 0), 0),
    [targets, guesses]
  );
  const numGuesses = guesses.length;
  const practice = useSelector((s) => s.game.practice);
  const Sedecordletitle = practice
    ? `Practice Sedecordle`
    : `Sedecordle #${id}`;
  const SedecOrderTitle = window.location.pathname === '/sedec-order/practice' ? (
    <>
      free <span style={{ color: "#33bb33" }}>sedec</span>
      <span style={{ color: "rgb(255, 204, 0)" }}>order</span>
    </>
  ) : (
    <>
      <span style={{ color: "#33bb33", fontWeight: '600' }}>Sedec</span>
      <span style={{ color: "rgb(255, 204, 0)", fontWeight: '600' }}>order</span> #{id}
    </>
  );
  const gameOver = useSelector((s) => s.game.gameOver);
  const extraGuessesNum =
    NUM_GUESSES - NUM_BOARDS - (numGuesses - boardsCompleted);
  const cannotWin = extraGuessesNum < 0;
  const extraGuesses =
    extraGuessesNum > 0 ? "+" + extraGuessesNum : extraGuessesNum;

  // Refs so that the buttons are blurred on press
  // so that pressing enter again does not cause the
  // button to be activated again
  const practiceRef = useRef<HTMLButtonElement>(null);
  const newRef = useRef<HTMLButtonElement>(null);
  const backRef = useRef<HTMLButtonElement>(null);
  const handlePracticeClick = () => {
    practiceRef.current?.blur();
    // const id = MersenneTwister().u32();
    // dispatch(startGame({ id, practice: true }));
    // dispatch(startPracticeGame({ id, practice: false }));
  };
  const handleNewClick = () => {
    newRef.current?.blur();
    const res = window.confirm(
      "Are you sure you want to start a new practice Sedecordle?\n" +
        "(Your current progress will be lost)"
    );
    if (!res) return;
    const id = MersenneTwister().u32();
    dispatch(startPracticeGame({ id, practice: true }));
    window.location.href = window.location.pathname;
  };
  const handleBackClick = () => {
    backRef.current?.blur();
    const res = window.confirm(
      "Are you sure you want to exit practice mode?\n" +
        "(Your current progress will be lost)"
    );
    if (!res) return;
    loadGameFromLocalStorage(dispatch);
  };

  // ctrl/cmd + shift + r keyboard shortcut to reset practice mode quickly
  const [reset, setReset] = useState(false);
  useEffect(() => {
    const handler = (k: KeyboardEvent) => {
      const useCmdKey = navigator.platform.match(/mac|iphone|ipad/i);
      if (
        practice &&
        k.key === "r" &&
        !k.shiftKey &&
        !k.altKey &&
        ((useCmdKey && !k.ctrlKey && k.metaKey) ||
          (!useCmdKey && k.ctrlKey && !k.metaKey))
      ) {
        k.preventDefault();
        const id = MersenneTwister().u32();
        dispatch(startGame({ id, practice: true }));
        setReset(true);
        setTimeout(() => setReset(false), 500);
      }
    };
    window.addEventListener("keydown", handler);
    return () => {
      window.removeEventListener("keydown", handler);
    };
  }, [dispatch, practice]);

  // Fullscreen
  const [fullscreen, setFullscreen] = useState(isFullscreen);
  useEffect(() => {
    const handler = () => {
      setFullscreen(isFullscreen);
    };
    document.addEventListener("fullscreenchange", handler);
    document.addEventListener("webkitfullscreenchange", handler);
    return () => {
      document.removeEventListener("fullscreenchange", handler);
      document.removeEventListener("webkitfullscreenchange", handler);
    };
  }, []);
  const handleFullscreenClick = () => {
    if (isFullscreen()) {
      exitFullscreen();
    } else {
      enterFullscreen();
    }
  };

  const switchPage = (link: any) => {
    localStorage.setItem("prevUrl", window.location.pathname);
    if (window.location.pathname === '/sedec-order') {
      window.location.href = '/sedec-order/about';
    } else if (window.location.href = '/sedec-order/practice/about') {
      window.location.href = '/sedec-order/practice/about';
    }
    
  };

  const PracticePage = (link: any) => {
    localStorage.setItem("prevUrl", link);
    window.location.href = link;
  };

  const PracticeBackPage = (link: any) => {
    if (localStorage.getItem("prevUrl") === "practice") {
      localStorage.setItem("prevUrl", "/");
      window.location.href = "/";
    } else {
      localStorage.setItem("prevUrl", link);
      window.location.href = link;
    }
  };

  useEffect(() => {
    if (localStorage.getItem("prevUrl") === "practice") {
      handlePracticeClick();
      localStorage.setItem("prevUrl", "/");
    }

    if (localStorage.getItem("prevUrl") === "/sedecordle/practice") {
      handlePracticeClick();
      localStorage.setItem("prevUrl", "/sedecordle");
    }

    if (window.location.pathname === "/sedecordle/practice") {
      handlePracticeClick();
      if (localStorage.getItem("prevUrl") === "") {
        localStorage.setItem("prevUrl", "/");
      }
    }

    if (localStorage.getItem("prevUrl") === "/sedec-order/practice") {
      handlePracticeClick();
      localStorage.setItem("prevUrl", "/sedec-order");
    }

    if (window.location.pathname === "/sedec-order/practice") {
      handlePracticeClick();
      if (localStorage.getItem("prevUrl") === "") {
        localStorage.setItem("prevUrl", "/");
        handlePracticeClick();
      }
    }
  }, []);

  return (
    <div className="header">
      <div className="row-1">
        {window.location.pathname === '/sedec-order/practice' ? (
          <>
            <button
              style={{width: '25%'}}
              className="mode-switch"
              ref={backRef}
              // onClick={() => PracticeBackPage(localStorage.getItem("prevUrl"))}
              onClick={() => window.location.href = '/'}
            >
              back
            </button>
            <button
              style={{width: '20%'}}
              className="mode-switch"
              ref={newRef}
              onClick={handleNewClick}
            >
              new
            </button>
            {/* <button onClick={()=> window.location.href = '/'} className="home-switch bg-white dark:bg-custom text-black dark:text-white">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            </button> */}
          </>
        ) : (
          <>
            <button
            style={{width: '25%'}}
              className="mode-switch"
              ref={practiceRef}
              // onClick={() =>
              //   PracticePage(`${window.location.pathname}/practice`)
              // }
              onClick={() => window.location.href = '/'}
            >
              back
            </button>

            {/* <button onClick={()=> window.location.href = '/'} className="home-switch mode-switch bg-white dark:bg-custom text-black dark:text-white">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            </button> */}

          </>
        )}
        <p className="title">
          {window.location.pathname === "/sedec-order" ||
          window.location.pathname === "/sedec-order/practice"
            ? SedecOrderTitle
            : Sedecordletitle}
        </p>
        {/* <button className="icon" onClick={() => dispatch(showStatsPopup())}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-7 w-7 text-black dark:text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
        </button> */}
        {/* <button className="icon" onClick={() => dispatch(showAboutPopup())}> */}
        <button className="icon" onClick={() => switchPage("about")}> 
          <img className="rounded-full bg-black" src={helpSvg} alt="Help" />
        </button>
        {/* <button className="icon" onClick={() => dispatch(showSettingsPopup())}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-7 w-7 text-black dark:text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
        </button> */}
        <button className="icon" onClick={handleFullscreenClick}>
          <img
            src={fullscreen ? fullscreenExitSvg : fullscreenSvg}
            alt="Go Fullscreen"
          />
        </button>
        {/* <button className="icon">
          <Switcher />
        </button> */}
      </div>
      <div className="row-2">
        <HeaderScroll />
        {/* <p>
          Boards Complete: {boardsCompleted}/{NUM_BOARDS}
        </p>
        <Timer showResetText={reset} />
        <p className={cn(cannotWin && !gameOver && "cannot-win")}>
          Guesses Used: {numGuesses}/{NUM_GUESSES} ({extraGuesses})
        </p> */}
      </div>
    </div>
  );
}

// type TimerProps = {
//   showResetText: boolean;
// };
// function Timer(props: TimerProps) {
//   const [flipFlop, setFlipFlop] = useState(false);
//   const showTimer = useSelector((s) => s.settings.showTimer);
//   const startTime = useSelector((s) => s.game.startTime);
//   const endTime = useSelector((s) => s.game.endTime);
//   const gameOver = useSelector((s) => s.game.gameOver);
//   const hasFirstGuess = useSelector((s) => s.game.guesses.length > 0);
//   const timeElapsed = useMemo(() => {
//     if (gameOver) {
//       return formatTimeElapsed(endTime - startTime);
//     } else if (!hasFirstGuess) {
//       return formatTimeElapsed(0);
//     } else {
//       return formatTimeElapsed(new Date().getTime() - startTime);
//     }
//   }, [startTime, endTime, hasFirstGuess, flipFlop, gameOver]);
//   useEffect(() => {
//     if (!showTimer) return;
//     const interval = setInterval(() => {
//       setFlipFlop((x) => !x);
//     }, 25);
//     return () => clearInterval(interval);
//   }, [showTimer, flipFlop]);

//   if (props.showResetText) {
//     return <p className="timer">New Game</p>;
//   }
//   if (!showTimer) {
//     return <p />;
//   }
//   return <p className="timer">{timeElapsed}</p>;
// }
